import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FormField } from "pages/GenericOrder/ui-form/form-field"
import { ModalForm } from "pages/GenericOrder/ui-form/modal-form"
import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { z } from "zod"
import { toast } from "sonner"
import { useParams } from "react-router-dom"
import { uploadInvoices } from "pages/GenericOrderDetailsPage/apis"
import { convertFileToBase64 } from "pages/CreateRemit/documentation"
import { Img } from "components"

const schema = z.object({
    invoice:
        z.object({
            traveler_id: z.string(),
            invoice_file: z
                .instanceof(File, { message: 'Please select a valid file' })
                .refine(file => file.size <= 5 * 1024 * 1024, {
                    message: 'File size must be less than 5MB',
                })
                .refine(
                    file => ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type),
                    {
                        message: 'Only PDF, JPEG, and PNG files are allowed',
                    }
                ),
        })
})

type FieldValues = z.infer<typeof schema>

interface Traveler {
    id: string
    name: string
    status: string
}

interface BulkUploadProps {
    children: React.ReactNode
    customers: Traveler[];
    doc_uuid?: string;
}

export default function BulkUpload({ children, customers, doc_uuid }: BulkUploadProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { id } = useParams()
    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation({
        mutationFn: uploadInvoices,
        onSuccess: () => {
            toast.success('Invoice uploaded successfully')
            queryClient.invalidateQueries(['orderDetails'])
            setIsOpen(false)
        },
        onError: (error: any) => {
            toast.error(`Failed to upload invoices: ${error.message || 'Unknown error'}`)
        }
    })

    const handleSubmit = async (data: FieldValues) => {
        // Filter out invoices without files
        const base64 = await convertFileToBase64(data.invoice.invoice_file)
        if (doc_uuid) {
            mutate({
                invoice: base64,
                doc_uuid: doc_uuid,
            })
        }
        else
            mutate({
                generic_order_details_id: Number(id),
                doc_id: 71, // Assuming this is a constant ID for the invoice document type
                invoice: base64,
                doc_uuid: '',
                customers_id: Number(data.invoice.traveler_id),
            })

    }

    return (
        <>
            <Button className="p-0" variant='default' onClick={() => setIsOpen(true)}>
                {children}
            </Button>

            <ModalForm<FieldValues>
                title="Upload Invoice"
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false)
                }}
                onSubmit={handleSubmit}
                submitButtonText={doc_uuid ? "Re-upload" : "Upload"}
                schema={schema}
                defaultValues={undefined}
                isLoading={isLoading}
                skipDirtyCheck={true}
            >
                {(form) => {
                    const selectedFiles = form.watch('invoice') || {}
                    return (
                        <div className="py-4">
                            <div className="max-h-96 overflow-y-auto">
                                <div className="w-full gap-4">
                                    <div className="w-full">
                                        <FormField
                                            form={form}
                                            name="invoice"
                                            render={({ field }) => (
                                                <div className="flex items-center gap-3 w-full">
                                                    <div
                                                        className={`py-1.5 px-3.5 w-full justify-between rounded-md flex border border-[#DCDEE2] ${selectedFiles?.invoice_file?.name ? "bg-[#F2F7FF]" : "bg-[#fff]"}`}
                                                    >
                                                        <div className="flex gap-3 items-center overflow-hidden">
                                                            <Img src="/images/remit/file.svg" alt="" />
                                                            <p className=" text-ellipsis truncate text-[#666666] text-sm font-normal">
                                                                {selectedFiles.invoice_file?.name || "Upload Invoice "}
                                                                {" "}
                                                                {selectedFiles.invoice_file?.name &&
                                                                    <span className="text-xs text-gray-500">
                                                                        ({Math.round(selectedFiles.invoice_file.size / 1024)} KB)
                                                                    </span>
                                                                }
                                                            </p>
                                                        </div>
                                                        <label

                                                            htmlFor="invoice.invoiceFile"
                                                            className=" cursor-pointer text-blue-700 border border-blue-700 text-white ml-2 px-4 py-2 rounded-lg whitespace-nowrap"
                                                        >
                                                            {selectedFiles.invoice_file?.name ? "Re-upload" : "Upload"}
                                                        </label>
                                                        <Input
                                                            type="file"
                                                            id="invoice.invoiceFile"
                                                            name="invoice.invoiceFile"
                                                            className="hidden"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    field.onChange({
                                                                        traveler_id: customers[0]?.id || '',
                                                                        invoice_file: file
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }}
            </ModalForm>
        </>
    )
}